import { Check, Close, ExpandMore } from '@mui/icons-material';
import { Accordion, AccordionDetails, Typography } from '@mui/material';
import { FormikErrors, FormikTouched, useFormikContext } from 'formik';
import { DateTime } from 'luxon';
import { AccordionPersonForm } from 'shell/view-models/accordion-person-form';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import {
  SubscriptionAccordionGenerality,
  CommonInterfaceSubscription,
} from 'subscription/view-models/common-interface-subscription';
import FormGenerality from '../../shared/generality/form';
import { __PERSON_API_URL__ } from 'shell/constants/environment';
import { PersonalDataSearchVM } from 'shell/view-models/personal-data';
import ResearchSipoPerson from 'shell/components/shared/research-sipo-accordion';
import { transformCivilStatus } from 'shell/utils/consult-person/response-transformer/marital-status/civil-status';
import { transformGenerality } from 'shell/utils/consult-person/response-transformer/registry-office/generality';
import { SubjectData } from 'shell/models/person';
import { transformBirthdayAnpr05Document } from 'shell/utils/consult-person/response-transformer/registry-office/birthday-document-anpr-05';

interface AccordionGeneralityProps {
  disabled?: boolean;
  isEditMode?: boolean;
  isCohabitation?: boolean;
  isAire?: boolean;
  redirectPath?: string;
  url?: string;
}

const hasNestedErrors = (errors: unknown): boolean => {
  if (!errors) return false;
  if (typeof errors === 'string') return true;
  if (typeof errors === 'object') return Object.keys(errors as Record<string, unknown>).length > 0;
  return false;
};

const AccordionGeneralityResearchSipo = ({
  disabled = false,
  isCohabitation = false,
  isAire = false,
  redirectPath = '/iscrizioni/aire-cittadinanza-nuova/persona-sipo',
  url = `${__PERSON_API_URL__}/non-residenti/ricerca-intestatario-cittadinanza-aire`,
}: AccordionGeneralityProps): React.ReactElement => {
  const [open, setOpen] = useState(true);

  const formContext = useFormikContext<CommonInterfaceSubscription>();

  const onFieldGeneralityChange = useCallback(
    (key, value) => {
      formContext.setFieldValue('generality', {
        ...formContext.values.generality,
        [key]: value,
      });
    },
    [formContext.values.generality, formContext.setFieldValue]
  );

  const onChangeValues = useCallback(
    (values) => {
      formContext.setFieldValue('generality', {
        ...formContext.values.generality,
        ...(values || {}),
      });
    },
    [formContext.values.generality, formContext.setFieldValue]
  );

  const onFieldPersonChange = useCallback(
    (key, value) => {
      formContext.setFieldValue('person', {
        ...formContext.values.person,
        [key]: value,
      });
    },
    [formContext.values.person, formContext.setFieldValue]
  );

  const resetForm = useCallback(() => {
    formContext.setValues({
      ...formContext.values,
      person: {},
      generality: {},
    });
  }, [formContext.values, formContext.setValues]);

  const errorGeneralityPerson = useMemo(
    () =>
      hasNestedErrors(formContext.errors.person) || hasNestedErrors(formContext.errors.generality),
    [formContext.errors.person, formContext.errors.generality]
  );

  const onSearchPersonEnd = useCallback(
    (fields: PersonalDataSearchVM) => {
      const formattedGenerality = fields.generality || {};
      const civilStatus = transformCivilStatus(fields.subjectData || {});
      const generalityFatherhood = transformGenerality(
        fields.subjectData?.paternita as SubjectData
      );

      const generalityMotherhood = transformGenerality(
        fields.subjectData?.maternita as SubjectData
      );

      const birthAct = transformBirthdayAnpr05Document(fields.subjectData || {});

      const firstCittadinanzaDate = fields.subjectData?.cittadinanza?.[0]?.dataValidita;

      const idSchedaSoggettoAnprFromSearch = fields.subjectData?.generalita?.idSchedaSoggettoANPR;
      const hasValidIdAnprFromSearch =
        idSchedaSoggettoAnprFromSearch && idSchedaSoggettoAnprFromSearch !== '0';

      formContext.setValues({
        ...(formContext.values || {}),
        preservedIdSchedaSoggettoAnpr: hasValidIdAnprFromSearch
          ? idSchedaSoggettoAnprFromSearch
          : formContext.values?.preservedIdSchedaSoggettoAnpr,
        oldCitizenship: fields.subjectData?.cittadinanza,
        citizenshipValidityDate: firstCittadinanzaDate
          ? DateTime.fromISO(firstCittadinanzaDate)
          : null,
        civilStatus,
        birthdayDocument: birthAct,
        fatherhood: generalityFatherhood
          ? {
              generality: generalityFatherhood,
            }
          : undefined,
        motherhood: generalityMotherhood
          ? {
              generality: generalityMotherhood,
            }
          : undefined,
        generality: {
          fromResearch: true,
          filter: undefined,
          tablePersons: undefined,
          ...formattedGenerality,
        },
        person: {
          name: formattedGenerality.name || '',
          surname: formattedGenerality.surname || '',
          withoutName: formattedGenerality.withoutName || false,
          withoutSurname: formattedGenerality.withoutSurname || false,
          fiscalCode: formattedGenerality.fiscalCode || '',
        },
        subjectData: fields.subjectData,
      });
    },
    [formContext.values]
  );

  return (
    <Fragment>
      <Accordion
        expanded={open}
        onChange={() => {
          setOpen(!open);
        }}
      >
        <AccordionSummary
          aria-controls="panel-generality-content"
          id="panel-generality-header"
          expandIcon={<ExpandMore />}
        >
          {errorGeneralityPerson ? <Close color="error" /> : <Check color="success" />}
          <Typography>Generalità</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <ResearchSipoPerson
            onSearchEnd={onSearchPersonEnd}
            redirectPath={redirectPath}
            url={url}
          />
          <FormGenerality
            isCohabitation={isCohabitation}
            disabled={disabled /*|| formContext.values.generality?.fromResearch*/}
            onChangePerson={onFieldPersonChange}
            onChangeGenerality={onFieldGeneralityChange}
            onChangeValues={onChangeValues}
            person={formContext.values.person}
            generality={formContext.values.generality}
            errorsGenerality={
              formContext.errors.generality as FormikErrors<SubscriptionAccordionGenerality>
            }
            errorsPerson={formContext.errors.person as FormikErrors<AccordionPersonForm>}
            touchedPerson={
              formContext.touched.person as unknown as FormikTouched<AccordionPersonForm>
            }
            touchedGenerality={
              formContext.touched
                .generality as unknown as FormikTouched<SubscriptionAccordionGenerality>
            }
            resetForm={resetForm}
            isAire={isAire}
          />
        </AccordionDetails>
      </Accordion>
    </Fragment>
  );
};

AccordionGeneralityResearchSipo.displayName = 'Accordion generality component';

export default AccordionGeneralityResearchSipo;
